/**
 * FO-301 (batch-32) — Servicekoffer: SK-nummerreeks, standaard-inhoud (pakket)
 * + per-koffer afwijkingen, uitrol-wizard en portal-weergave.
 *
 * De koffer is GEEN aparte administratie: het is een `VoorraadUniekItem`-
 * exemplaar van een traceerbaar koffer-artikel. Het koffernummer is de
 * `assetTag` in de SK-reeks. De inhoud = het gekoppelde `GereedschapsPakket`
 * (één bron) met per-koffer afwijkingen erop toegepast.
 *
 * Conventies (gelijk aan de rest van packages/types):
 *   - ID-referenties in Zod: `z.string().min(1)` (NIET `.cuid()` — prod heeft
 *     legacy UUID-format ids).
 *   - Optioneel-nullable velden: `.optional().nullable()`.
 *   - Geen kostvelden in de portal-DTO's (geen inkoop-/verkoopprijs naar de monteur).
 */
import { z } from 'zod'

// ─── Enums ───────────────────────────────────────────────────────────────────

/** Soort afwijking van de standaard-inhoud (spiegelt `VoorraadInhoudAfwijkingType`). */
// batch-70 uitrusting-controle (FO-531): NIET_ONTVANGEN = nooit gekregen (gemeld in de uitrustingscontrole).
export const VOORRAAD_INHOUD_AFWIJKING_TYPE = ['ONTBREEKT', 'DEFECT', 'VERVANGEN', 'EXTRA', 'NIET_ONTVANGEN'] as const
export type VoorraadInhoudAfwijkingType = (typeof VOORRAAD_INHOUD_AFWIJKING_TYPE)[number]

export const VOORRAAD_INHOUD_AFWIJKING_TYPE_LABELS: Record<VoorraadInhoudAfwijkingType, string> = {
  ONTBREEKT: 'Ontbreekt',
  DEFECT:    'Defect',
  VERVANGEN: 'Vervangen',
  EXTRA:     'Extra',
  NIET_ONTVANGEN: 'Niet ontvangen',
}

/**
 * Status van één regel in de afgeleide werkelijke inhoud van een koffer.
 * STANDAARD = conform pakket; de overige spiegelen de afwijking-types.
 */
export const KOFFER_INHOUD_REGEL_STATUS = [
  'STANDAARD',
  'ONTBREEKT',
  'DEFECT',
  'VERVANGEN',
  'EXTRA',
  // batch-70 uitrusting-controle (FO-531): gemeld als nooit ontvangen.
  'NIET_ONTVANGEN',
] as const
export type KofferInhoudRegelStatus = (typeof KOFFER_INHOUD_REGEL_STATUS)[number]

export const KOFFER_INHOUD_REGEL_STATUS_LABELS: Record<KofferInhoudRegelStatus, string> = {
  STANDAARD: 'Standaard',
  ONTBREEKT: 'Ontbreekt',
  DEFECT:    'Defect',
  VERVANGEN: 'Vervangen',
  EXTRA:     'Extra',
  NIET_ONTVANGEN: 'Niet ontvangen',
}

/** Uitkomst per medewerker in de uitrol-wizard. */
export const KOFFER_UITROL_STATUS = ['AANGEMAAKT', 'OVERGESLAGEN', 'FOUT'] as const
export type KofferUitrolStatus = (typeof KOFFER_UITROL_STATUS)[number]

// ─── DTOs ───────────────────────────────────────────────────────────────────

/** Eén geregistreerde afwijking op de standaard-inhoud van een koffer (backoffice). */
export interface KofferInhoudAfwijkingDTO {
  id: string
  type: VoorraadInhoudAfwijkingType
  /** Standaard-regel waar dit op slaat (ONTBREEKT/DEFECT/VERVANGEN), of null. */
  pakketRegelId: string | null
  /** Artikel-id bij een EXTRA-afwijking, of null. */
  voorraadArtikelId: string | null
  /** Geresolveerde artikelnaam (van pakketRegel of extra-artikel). */
  artikelNaam: string
  aantal: number
  notitie: string | null
  createdAt: string
}

/**
 * Eén regel in de afgeleide werkelijke inhoud (backoffice-detail). Combineert de
 * standaard-pakketregels met de afwijkingen: elke standaard-regel krijgt een
 * status, EXTRA-afwijkingen komen als losse regels erbij.
 */
export interface KofferInhoudRegelDTO {
  /** Pakketregel-id (STANDAARD/ONTBREEKT/DEFECT/VERVANGEN) of null bij EXTRA. */
  pakketRegelId: string | null
  voorraadArtikelId: string
  artikelNaam: string
  categorie: string
  maat: string | null
  /** Standaard-aantal uit het pakket (of extra-aantal bij EXTRA). */
  aantal: number
  status: KofferInhoudRegelStatus
  /** Optionele afwijking-notitie (defect/vervangen/extra). */
  notitie: string | null
}

/**
 * Volledige inhoud van één koffer-exemplaar (backoffice): het gekoppelde
 * standaard-pakket, de losse afwijkingen én de afgeleide werkelijke inhoud.
 */
export interface KofferInhoudDetailDTO {
  uniekItemId: string
  /** Het koffernummer (assetTag) — kan null zijn als een exemplaar (nog) geen SK-tag heeft. */
  koffernummer: string | null
  /** Het gekoppelde standaard-inhoud-pakket, of null als het artikel er geen heeft. */
  pakketId: string | null
  pakketNaam: string | null
  /** De standaard-inhoud-regels uit het pakket (één bron). */
  standaardRegels: KofferInhoudRegelDTO[]
  /** De geregistreerde afwijkingen (ruwe lijst voor beheer). */
  afwijkingen: KofferInhoudAfwijkingDTO[]
  /** Afgeleide werkelijke inhoud = standaard + afwijkingen toegepast. */
  werkelijkeInhoud: KofferInhoudRegelDTO[]
  /** true wanneer er een pakket is én geen enkele afwijking (compleet conform standaard). */
  compleetConformStandaard: boolean
}

/** Resultaat per medewerker na een uitrol-run. */
export interface KofferUitrolResultaatRegelDTO {
  employeeId: string
  employeeNaam: string
  status: KofferUitrolStatus
  /** Toegekend koffernummer (bij AANGEMAAKT). */
  koffernummer: string | null
  /** Id van het aangemaakte koffer-exemplaar (bij AANGEMAAKT). */
  uniekItemId: string | null
  /** Toelichting bij OVERGESLAGEN/FOUT (bv. "had al een koffer"). */
  waarschuwing: string | null
}

export interface KofferUitrolResultaatDTO {
  regels: KofferUitrolResultaatRegelDTO[]
  aantalAangemaakt: number
  aantalOvergeslagen: number
}

/** Portal: één inhoud-regel van de eigen koffer — ZONDER kostvelden. */
export interface MijnKofferInhoudRegelDTO {
  naam: string
  categorie: string
  maat: string | null
  aantal: number
  status: KofferInhoudRegelStatus
}

/** Een koffer-artikel (traceerbaar + met standaard-inhoud-pakket) voor de wizard-picker. */
export interface KofferArtikelOptieDTO {
  id: string
  artikelNummer: string
  naam: string
  pakketId: string
  pakketNaam: string
  /** Aantal standaard-inhoud-regels in het pakket. */
  aantalRegels: number
  /** Aantal actief uitgegeven koffers van dit artikel. */
  aantalUitgegeven: number
}

/** Portal: de eigen koffer van de monteur — ZONDER kostvelden. */
export interface MijnKofferDTO {
  id: string
  koffernummer: string | null
  artikelNaam: string
  uitgereiktOp: string | null
  compleetConformStandaard: boolean
  inhoud: MijnKofferInhoudRegelDTO[]
}

// ─── Zod schemas ────────────────────────────────────────────────────────────

/**
 * Uitrol-wizard: registreer + nummer + wijs een koffer toe aan elke medewerker
 * in de gekozen VOLGORDE (array-volgorde = SK-toekenningsvolgorde). Medewerkers
 * die al een actieve koffer hebben worden overgeslagen tenzij `geforceerd`.
 */
export const kofferUitrolSchema = z.object({
  /** Het koffer-artikel (traceerbaar, met standaard-inhoud-pakket). */
  artikelId: z.string().min(1),
  /** Geordende medewerker-ids — de volgorde bepaalt de SK-nummers. */
  employeeIds: z.array(z.string().min(1)).min(1).max(500),
  /** Forceer ook voor medewerkers die al een koffer hebben (tijdelijke 2e koffer). */
  geforceerd: z.boolean().optional().default(false),
  /** Uitgifte-reden (default in de service). */
  reden: z.string().max(500).optional().nullable(),
})
export type KofferUitrolInput = z.infer<typeof kofferUitrolSchema>

/**
 * Individuele koffer registreren (nieuwe monteur): auto-SK, of een expliciet
 * vrij nummer binnen de reeks. Optioneel direct uitgeven aan een medewerker.
 */
export const registreerKofferSchema = z.object({
  artikelId: z.string().min(1),
  /** Direct uitgeven aan deze medewerker (optioneel). */
  employeeId: z.string().min(1).optional().nullable(),
  /**
   * Expliciet koffernummer (voor het naregistreren van al-gestickerde koffers).
   * Moet in de SK-reeks vallen (SK + cijfers) en vrij zijn; leeg = auto-nummer.
   */
  koffernummer: z
    .string()
    .regex(/^SK[0-9]+$/, 'Koffernummer moet in de SK-reeks vallen (bv. SK1023)')
    .optional()
    .nullable(),
  reden: z.string().max(500).optional().nullable(),
})
export type RegistreerKofferInput = z.infer<typeof registreerKofferSchema>

/**
 * Nieuwe inhoud-afwijking op een koffer. XOR-refine: ONTBREEKT/DEFECT/VERVANGEN
 * verwijzen naar een `pakketRegelId` (welke standaard-regel), EXTRA naar een
 * `voorraadArtikelId` (welk extra artikel). Precies één van beide is gevuld.
 */
export const inhoudAfwijkingCreateSchema = z
  .object({
    type: z.enum(VOORRAAD_INHOUD_AFWIJKING_TYPE),
    pakketRegelId: z.string().min(1).optional().nullable(),
    voorraadArtikelId: z.string().min(1).optional().nullable(),
    aantal: z.number().int().min(1).max(9999).optional().default(1),
    notitie: z.string().max(2000).optional().nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.type === 'EXTRA') {
      if (!val.voorraadArtikelId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Een extra-afwijking vereist een artikel (voorraadArtikelId)',
          path: ['voorraadArtikelId'],
        })
      }
      if (val.pakketRegelId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Een extra-afwijking mag geen pakketRegelId hebben',
          path: ['pakketRegelId'],
        })
      }
    } else {
      // ONTBREEKT / DEFECT / VERVANGEN
      if (!val.pakketRegelId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Deze afwijking vereist een standaard-regel (pakketRegelId)',
          path: ['pakketRegelId'],
        })
      }
      if (val.voorraadArtikelId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Deze afwijking mag geen voorraadArtikelId hebben',
          path: ['voorraadArtikelId'],
        })
      }
    }
  })
export type InhoudAfwijkingCreateInput = z.infer<typeof inhoudAfwijkingCreateSchema>

/** Wijzig een bestaande afwijking (alleen aantal + notitie; type/doel liggen vast). */
export const inhoudAfwijkingUpdateSchema = z.object({
  aantal: z.number().int().min(1).max(9999).optional(),
  notitie: z.string().max(2000).optional().nullable(),
})
export type InhoudAfwijkingUpdateInput = z.infer<typeof inhoudAfwijkingUpdateSchema>
